import React from 'react';
import { Link } from 'react-router-dom';
import { VIDEO_PORTFOLIO_ENABLED } from '../config/features';
import { BlockPrintPattern, ArchDivider } from './brand/Jaipur';

const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFB53F] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0E0F14] rounded-sm';
const linkClass = `text-[#E9E2D2] hover:text-[#FFB53F] transition-colors duration-200 text-sm sm:text-[15px] inline-block py-1 ${focusRing}`;
const socialClass = 'w-10 h-10 bg-white/[0.06] border border-white/10 rounded-full flex items-center justify-center transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFB53F]';

const WHATSAPP_URL =
  'https://wa.me/919587773274?text=' +
  encodeURIComponent('Hi Apnaaapan, I was checking out your website and want to talk about growing my brand.');

const SERVICES = [
  { to: '/services/social-media', label: 'Social Media Marketing' },
  { to: '/services/branding', label: 'Branding & Identity' },
  { to: '/services/design', label: 'Design & Creative' },
  { to: '/services/web-development', label: 'Web Development' },
  { to: '/services/marketing', label: 'Marketing Strategy' },
];

const QUICK_LINKS = [
  { to: '/partner-with-us', label: 'Partner with Us' },
  { to: '/work-with-us', label: 'Careers' },
  { href: 'https://with.apnaaapan.com', label: 'With.apnaaapan Community' },
  { to: '/blog', label: 'Playbooks' },
];

const INFO_LINKS = [
  { to: '/our-story', label: 'Our Story' },
  { to: '/work', label: 'Case Studies' },
  { to: '/website-portfolio', label: 'Website Portfolio' },
  { to: '/design-portfolio', label: 'Design & Branding' },
  ...(VIDEO_PORTFOLIO_ENABLED ? [{ to: '/portfolio', label: 'Video Portfolio' }] : []),
  { to: '/graphic-portfolio', label: 'Graphic Portfolio' },
  { to: '/contact', label: 'Contact' },
];

const LinkColumn = ({ title, links }) => (
  <div className="lg:col-span-1 xl:col-span-2">
    <h3 className="text-[#A39C8E] font-semibold text-xs uppercase tracking-[0.16em] mb-4 sm:mb-5">{title}</h3>
    <ul className="space-y-1 sm:space-y-2">
      {links.map((link) => (
        <li key={link.to || link.href}>
          {link.href ? (
            <a href={link.href} className={linkClass}>
              {link.label}
            </a>
          ) : (
            <Link to={link.to} className={linkClass}>
              {link.label}
            </Link>
          )}
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => {
  return (
    <footer className="relative isolate overflow-hidden bg-[#0E0F14] text-[#E9E2D2] px-4 sm:px-6 md:px-8 pb-10 sm:pb-12">
      {/* Jaipur block-print texture + saffron glow */}
      <BlockPrintPattern className="text-[#FFB53F] -z-10" opacity={0.05} size={64} />
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 left-1/2 -z-10 h-80 w-[48rem] max-w-[140vw] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(242,107,42,0.22),transparent)]" />
      <ArchDivider className="absolute inset-x-0 top-0 text-apn-bg rotate-180" />

      <div className="max-w-7xl mx-auto pt-14 sm:pt-20">
        {/* WhatsApp-first contact band */}
        <div className="mb-12 sm:mb-14 flex flex-col gap-6 rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 md:flex-row md:items-center md:justify-between">
          <div className="max-w-xl">
            <p className="text-[#FFB53F] text-xs font-semibold uppercase tracking-[0.18em] mb-2">Made in Jaipur, working everywhere</p>
            <h2 className="font-display text-[#F6EFE0] text-2xl sm:text-3xl md:text-4xl font-extrabold leading-tight tracking-tight">
              Got a brand to grow? Say hi on WhatsApp.
            </h2>
            <p className="mt-2 text-[#BDB5A6] text-sm sm:text-base">A real person from the team replies, usually within a few hours.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-cta="footer-whatsapp"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-semibold text-[#0B2E17] transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFB53F] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0E0F14]"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
              </svg>
              Chat on WhatsApp
            </a>
            <Link
              to="/book-call"
              data-cta="footer-book-call"
              className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-orange-500 to-yellow-400 px-6 py-3 font-semibold text-white shadow-lg transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFB53F] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0E0F14]"
            >
              Book a Free Strategy Call
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-12 gap-8 xl:gap-12">

          {/* Column 1 - Logo, contact and social media */}
          <div className="lg:col-span-1 xl:col-span-3">
            <div className="mb-4 sm:mb-6">
              <img decoding="async"
                src="/images/Logo Icon A 4.webp"
                alt="Apnaaapan"
                loading="lazy"
                width="64"
                height="64"
                className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
              />
            </div>

            <div className="space-y-1 mb-4 sm:mb-6">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#A39C8E] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <a href="tel:+919587773274" className={`text-[#E9E2D2] text-sm sm:text-base hover:text-[#FFB53F] py-2 ${focusRing}`}>
                  +91 95877 73274
                </a>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#A39C8E] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href="mailto:grow@apnaaapan.com" className={`text-[#E9E2D2] text-sm sm:text-base whitespace-nowrap hover:text-[#FFB53F] py-2 ${focusRing}`}>
                  grow@apnaaapan.com
                </a>
              </div>
            </div>

            {/* Social Media Icons */}
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <a href={WHATSAPP_URL} aria-label="WhatsApp" target="_blank" rel="noopener noreferrer" className={`${socialClass} hover:bg-green-600`}>
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                </svg>
              </a>
              <a href="https://www.facebook.com/apnaaapan1/" aria-label="Facebook" target="_blank" rel="noopener noreferrer" className={`${socialClass} hover:bg-blue-600`}>
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              <a href="https://instagram.com/apnaaapan" aria-label="Instagram" target="_blank" rel="noopener noreferrer" className={`${socialClass} hover:bg-gradient-to-tr hover:from-pink-500 hover:to-violet-500`}>
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="none" stroke="currentColor" strokeWidth="2" />
                  <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
                  <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" />
                </svg>
              </a>
              <a href="https://x.com/apnaaapan" aria-label="X (Twitter)" target="_blank" rel="noopener noreferrer" className={`${socialClass} hover:bg-gray-600`}>
                <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
                </svg>
              </a>
              <a href="https://www.behance.net/apnaaapan" aria-label="Behance" target="_blank" rel="noopener noreferrer" className={`${socialClass} hover:bg-[#1769FF]`}>
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8.2 11.3c.9-.4 1.5-1.2 1.5-2.3 0-2.2-1.6-2.9-3.6-2.9H1v11.8h5.3c2.1 0 4.1-1 4.1-3.4 0-1.5-.7-2.7-2.2-3.2zM3.4 8.1h2.3c.9 0 1.7.3 1.7 1.3s-.6 1.4-1.5 1.4H3.4V8.1zm2.6 7.8H3.4v-3.2h2.6c1.1 0 1.8.5 1.8 1.6 0 1.2-.8 1.6-1.8 1.6zM15.7 8.9c-2.6 0-4.3 1.9-4.3 4.5 0 2.7 1.6 4.5 4.3 4.5 2 0 3.3-.9 4-2.9h-2.1c-.2.7-1.1 1.1-1.8 1.1-1.3 0-2-.8-2-2.1h6c.1-2.7-1.3-5.1-4.1-5.1zm-1.9 3.6c.1-1.1.8-1.8 1.9-1.8s1.7.7 1.8 1.8h-3.7zM13.9 6.7h4.6v1.1h-4.6z" />
                </svg>
              </a>
              <a href="https://youtube.com/@apnaaapan" aria-label="YouTube" target="_blank" rel="noopener noreferrer" className={`${socialClass} hover:bg-red-600`}>
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </a>
            </div>
          </div>

          <LinkColumn title="Services" links={SERVICES} />
          <LinkColumn title="Quick Links" links={QUICK_LINKS} />
          <LinkColumn title="Company" links={INFO_LINKS} />

          {/* Column 5 - Studio address */}
          <div className="xl:col-span-3 flex flex-col items-start xl:items-end">
            <h3 className="text-[#A39C8E] font-semibold text-xs uppercase tracking-[0.16em] mb-4 sm:mb-5">The studio</h3>
            <address className="not-italic mb-4 sm:mb-6 text-left xl:text-right">
              <p className="text-[#E9E2D2] text-sm leading-relaxed">
                136, Shree Shyam Tower,<br />
                Kesar Nagar, Sanganer,<br />
                Jaipur - 302020, Rajasthan, India
              </p>
            </address>
          </div>
        </div>

        {/* Bottom Section - Copyright & Privacy */}
        <div className="border-t border-white/10 mt-10 sm:mt-12 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className="text-[#A39C8E] text-xs sm:text-sm text-center sm:text-left">
            © {new Date().getFullYear()} Apnaaapan. All rights reserved.
          </div>
          <div className="text-[#A39C8E] text-xs sm:text-sm flex gap-4">
            <Link to="/terms-and-conditions" className={`hover:text-[#F6EFE0] transition-colors duration-200 py-2 ${focusRing}`}>Terms & Conditions</Link>
            <Link to="/privacy-policy" className={`hover:text-[#F6EFE0] transition-colors duration-200 py-2 ${focusRing}`}>Privacy Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
